import { useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useScreenStore } from '@/state/screenStore';
import { useSettingsStore } from '@/state/settingsStore';
import { hasSongs } from '@/storage/songsRepo';
import { LogoMark, MoonIcon, ShieldIcon, SunIcon } from '@/ui/icons';
import styles from './AppShell.module.css';
import { resolvedTheme } from './theme';
import { UpdatePrompt } from './UpdatePrompt';

function ThemeToggle() {
  const theme = useSettingsStore((s) => s.theme);
  const update = useSettingsStore((s) => s.update);
  const dark = resolvedTheme(theme) === 'dark';
  return (
    <button
      type="button"
      className={styles.iconButton}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={() => {
        update({ theme: dark ? 'light' : 'dark' });
      }}
    >
      {dark ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

export function AppShell() {
  const showLibrary = useScreenStore((s) => s.showLibrary);

  // Songs live only in this tab (ADR 0007): ask before a reload or close would lose them.
  // Browsers show their own wording, and only after the user has interacted with the page.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (hasSongs()) e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, []);

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link
            className={styles.logo}
            to="/"
            aria-label="KnowYourChord, go to library"
            onClick={showLibrary}
          >
            <span className={styles.logoTile}>
              <LogoMark />
            </span>
            <span className={styles.wordmark}>
              KnowYour<span className={styles.wordmarkAccent}>Chord</span>
            </span>
          </Link>
          <nav aria-label="Main" className={styles.nav}>
            <NavLink
              to="/"
              end
              onClick={showLibrary}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
            >
              Library
            </NavLink>
            <NavLink
              to="/tuner"
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
            >
              Tuner
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
            >
              Settings
            </NavLink>
          </nav>
          <span className={styles.privacyChip}>
            <ShieldIcon size={16} />
            Runs on this device
          </span>
          <ThemeToggle />
        </div>
      </header>
      <div className={styles.content}>
        <Outlet />
      </div>
      {import.meta.env.PROD && <UpdatePrompt />}
    </div>
  );
}
